import {DOCUMENT, inject, Injectable, signal} from '@angular/core';
import {OAuthService} from 'angular-oauth2-oidc';
import {firstValueFrom, of} from 'rxjs';
import {filter, map, timeout} from 'rxjs/operators';
import {loginState} from '../components/login-page.component';

/**
 * Refreshes the access token when it is needed, not only when angular-oauth2-oidc's timer says so.
 *
 * `setupAutomaticSilentRefresh()` schedules the refresh with a timer from the moment the token was
 * stored. A hidden tab or a sleeping machine holds that timer back, so it can fire well after the token
 * expired (on sannies: 15 minutes late), and the API authorizers reject expired tokens with a 401. So a
 * request makes sure the token is fresh first, and a tab that becomes visible again refreshes at once.
 */
@Injectable({providedIn: 'root'})
export class TokenRefreshService {
  /** Refresh this long before expiry — also absorbs clock skew between the browser and the authorizer. */
  static readonly MARGIN_MS = 60_000;

  private readonly oauth = inject(OAuthService);
  private readonly document = inject(DOCUMENT);
  private inFlight: Promise<boolean> | null = null;
  private signingIn = false;
  /** Why the login could not be restored at app start; the login page shows it next to its Sign in button. */
  readonly loginError = signal<string | null>(null);

  constructor() {
    const document = this.document;
    // angular-oauth2-oidc's own timer swallows a failed refresh and only reports it as this event.
    this.oauth.events.pipe(filter(event => event.type === 'token_refresh_error')).subscribe(() => this.signIn());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        void this.ensureFresh();
      }
    });
  }

  /** Resolves once the access token is good for at least MARGIN_MS; false if it could not be made so. */
  ensureFresh(): Promise<boolean> {
    if (!this.oauth.getRefreshToken()) {
      // not logged in (or no refresh grant): nothing to refresh with, the request goes as it is
      return Promise.resolve(this.oauth.hasValidAccessToken());
    }
    const expiresAt = this.oauth.getAccessTokenExpiration();
    if (expiresAt && expiresAt - Date.now() > TokenRefreshService.MARGIN_MS) {
      return Promise.resolve(true);
    }
    return this.refresh();
  }

  /**
   * Refresh now. Concurrent callers share one refresh, so a burst of requests exchanges the token once.
   * A refresh that fails (refused refresh token, HTTP error) means the login is gone: sign in again.
   */
  refresh(): Promise<boolean> {
    if (!this.inFlight) {
      this.inFlight = this.discoveryLoaded()
        // refreshToken() throws synchronously when the token endpoint is unknown, so call it inside a promise
        .then(loaded => loaded && new Promise(resolve => resolve(this.oauth.refreshToken())).then(() => true, () => {
          this.signIn();
          return false;
        }))
        .catch(() => false)
        .finally(() => this.inFlight = null);
    }
    return this.inFlight;
  }

  /**
   * App start (VTK-3596): discovery first, then the code-flow callback, then a refresh of an expired token,
   * and only then the library's refresh timer -- started earlier, an expired token made it call
   * refreshToken() before the token endpoint was known, which threw and left a blank page.
   * Resolves whether the user is logged in. Never rejects: false shows the login page (with `loginError`).
   */
  async start(): Promise<boolean> {
    try {
      await this.oauth.loadDiscoveryDocument();
      await this.oauth.tryLogin();
    } catch (error) {
      // No automatic sign-in here: an IdP that answers the callback with ?error=access_denied would answer
      // the next code flow the same way, and a full-page redirect loop has no in-memory guard. The login
      // page shows why and the user decides. Only a failed refresh of a live session signs in by itself.
      console.error('Login could not be restored', error);
      this.loginError.set(loginErrorText(error));
      return false;
    }
    if (!this.oauth.hasValidAccessToken() && this.oauth.getRefreshToken() && !await this.refresh()) {
      return false;  // refresh() has started the sign-in
    }
    this.oauth.setupAutomaticSilentRefresh();
    return this.oauth.hasValidAccessToken();
  }

  /**
   * The login is gone: forget its tokens and go to the identity provider, coming back to the current page.
   * Without the discovery document there is no login URL; the login page (false from start()) is left.
   */
  signIn(): void {
    if (this.signingIn) {
      return;
    }
    this.signingIn = true;
    const state = loginState(this.document.location.hash);
    this.oauth.logOut(true);  // true: clear the tokens here, without a round trip to the IdP's logout page
    if (this.oauth.discoveryDocumentLoaded) {
      try {
        this.oauth.initCodeFlow(state);
        return;
      } catch (error) {
        console.error('Could not start the login', error);
      }
    }
    this.signingIn = false;
  }

  /**
   * The token endpoint comes from the discovery document. A tab reopened with an expired token sends its
   * first requests (and fires visibilitychange) before `loadDiscoveryDocumentAndTryLogin()` has finished,
   * so wait for it; give up after DISCOVERY_WAIT_MS and let the request go (a 401 then leads to sign-in).
   */
  private discoveryLoaded(): Promise<boolean> {
    if (this.oauth.discoveryDocumentLoaded) {
      return Promise.resolve(true);
    }
    return firstValueFrom(this.oauth.events.pipe(
      filter(event => event.type === 'discovery_document_loaded'),
      map(() => true),
      timeout({first: TokenRefreshService.DISCOVERY_WAIT_MS, with: () => of(false)}),
    ));
  }

  /** How long a refresh waits for the discovery document before giving up. */
  static readonly DISCOVERY_WAIT_MS = 10_000;
}

/** The IdP's own words for a refused login (an OAuthErrorEvent carries the callback's error parameters). */
export function loginErrorText(error: any): string {
  const params = error?.params;
  const reason = params?.error_description || params?.error || error?.message || String(error);
  return `Sign-in failed: ${reason}`;
}
